import { useRef } from 'react'

import {
  Card,
  CircleLoader,
  ConfirmationModal,
  IconButton,
  PrimaryButton,
  Space,
  TertiaryButton,
  WithTooltip,
} from '@procurify/ui'
import { type FormikProps } from 'formik'

import { TabContentFooter } from '@webapp/components/TabContentFooter'
import { TabPanes } from '@webapp/components/TabPanes'
import { PageActions } from '@webapp/core/components/Page'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/ExpenseReportForm'
import { EditExpenseLineItemDrawer } from '@webapp/modules/expense/feature-sets/EditExpenseLineItemDrawer'
import { ExpenseCommentPopoverButton } from '@webapp/modules/expense/feature-sets/ExpenseView/components'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ExpenseCostSummaryPresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpenseCostSummaryPresenter'
import { ReviseExpenseReportPresenter } from '@webapp/modules/expense/features/revise-expense-report/presenters/ReviseExpenseReportPresenter'
import messages from './messages'
import { ExpenseReviseViewStyled } from './styles/ExpenseReviseViewStyled'
import { useExpenseReportReviseView } from './useExpenseReportReviseView'

export interface IExpenseReportReviseViewProps {
  uuid: string
}

export const ExpenseReportReviseView = ({
  uuid,
}: IExpenseReportReviseViewProps) => {
  const intl = useProcurifyIntl()
  const formRef = useRef<FormikProps<ExpenseReportFormSchema> | null>(null)

  const {
    data: {
      expense,
      isLoading,
      isReviseFormDefaultsLoading,
      initialValues,
      editingItem,
      drawerOpen,
      isSubmitting,
    },
    states: {
      isDuplicateConfirmOpen,
      isRequester,
      showComments,
      expenseContentTypeId,
      userId,
    },
    methods: {
      onLineItemEditClick,
      onLineItemEditSuccess,
      onLineItemEditCancel,
      onReviseSuccess,
      onReviseCancel,
      onSubmittingChange,
    },
    commands: { duplicateExpense, confirmDuplicate, closeDuplicateConfirm },
  } = useExpenseReportReviseView({ uuid })

  if (isLoading) {
    return <CircleLoader />
  }

  const hasLogs = !!expense?.hasLogs

  return (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
              />
            </WithTooltip>
          )}

          {hasLogs && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}
        </Space>
      </PageActions>

      <ExpenseReviseViewStyled>
        <Card>
          <Card.Content>
            <Space size='large'>
              <TabPanes activeKey='revise'>
                <TabPanes.Pane
                  label={intl.formatMessage(messages.reviseTabLabel)}
                  key='revise'
                >
                  {isReviseFormDefaultsLoading && <CircleLoader />}

                  {expense && !isReviseFormDefaultsLoading && (
                    <ReviseExpenseReportPresenter
                      expense={expense}
                      initialValues={initialValues}
                      formRef={formRef}
                      onSuccess={onReviseSuccess}
                      onEditItem={onLineItemEditClick}
                      onSubmittingChange={onSubmittingChange}
                    />
                  )}

                  {expense && (
                    <TabContentFooter isSticky>
                      <Space direction='vertical'>
                        <Space direction='horizontal' justify='end'>
                          <ExpenseCostSummaryPresenter
                            uuid={uuid}
                          />
                        </Space>

                        <Space
                          direction='horizontal'
                          justify='end'
                          align='center'
                          size='large'
                        >
                          <TertiaryButton
                            type='button'
                            onClick={onReviseCancel}
                            disabled={isSubmitting}
                          >
                            {intl.formatMessage(messages.cancelButton)}
                          </TertiaryButton>
                          <PrimaryButton
                            onClick={() => formRef.current?.submitForm()}
                            disabled={isSubmitting}
                          >
                            {intl.formatMessage(messages.resubmitButton)}
                          </PrimaryButton>
                        </Space>
                      </Space>
                    </TabContentFooter>
                  )}
                </TabPanes.Pane>
              </TabPanes>
            </Space>
          </Card.Content>
        </Card>
      </ExpenseReviseViewStyled>

      {editingItem && expense && (
        <EditExpenseLineItemDrawer
          drawerOpen={drawerOpen}
          onClose={onLineItemEditCancel}
          item={editingItem}
          expenseReportId={expense.id}
          requesterId={expense.requester.id}
          secondaryCategoryId={expense.secondaryCategory.id}
          onSuccess={onLineItemEditSuccess}
        />
      )}

      <ConfirmationModal
        open={isDuplicateConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.duplicateOverrideConfirmHeader)}
        message={intl.formatMessage(messages.duplicateOverrideConfirmMessage)}
        onConfirm={async (done) => {
          await confirmDuplicate()
          done()
        }}
        handleDismiss={closeDuplicateConfirm}
      />
    </>
  )
}

ExpenseReportReviseView.displayName = 'ExpenseReportReviseView'
